import React from 'react';
import { useDispatch } from 'react-redux';

import {
  useCompareContext,
  usePresetColorsContext,
  useRunsColorsContext,
} from '@neptune/compare-business-logic';
import { useAssociatedRunsContext } from '@neptune/entity-common-business-logic';
import { ENTITY_TYPE_TO_TEXT_MAP } from '@neptune/entity-common-domain';
import { DetailsDropdownMenuContent } from '@neptune/entity-common-ui';
import { useGenericModal } from '@neptune/shared/common-business-logic';
import { ColorPickerMenu } from '@neptune/shared/common-ui';
import { makeProjectIdentifier } from '@neptune/shared/core-project-util';
import {
  shouldSkipArchiveExperimentHeadConfirmation,
  useArchiveEntity,
} from '@neptune/shared/entity-business-logic';
import {
  ARCHIVE_EXPERIMENT_HEAD_CONFIRMATION_MODAL_TYPE,
  Entity,
  EntityDetailsModalData,
  EntityTypeToDetailsModalName,
  getEntityArchived,
  getEntityShortId,
  getEntityYodaJobUrl,
  getRunIdentificationPair,
} from '@neptune/shared/entity-domain';
import { useExperimentsModeContext } from '@neptune/shared/leaderboard-business-logic';
import { WithPermissions } from '@neptune/shared/project-domain';
import { ActionsMenu } from '@neptune/shared/venus-ui';

import { getIsExperimentHeadFromAttributes, KnownAttributes } from 'domain/experiment/attribute';
import { openConfirmationModal } from 'state/ui/global/confirmation-modal/actions';
import { addNotification } from 'state/ui/global/notification-popup/actions';
import { openManageTags } from 'state/views/manage-tags/actions';

const DEFAULT_MANAGE_TAGS_ATTRIBUTE_NAMES = [KnownAttributes.Tags, KnownAttributes.GroupTags];

export type DetailsDropdownMenuContentContainerProps = {
  name?: string;
  alias?: string;
  isAliasEditable?: boolean;
  isColorEditable?: boolean;
  isSaving?: boolean;
  onCopy?: () => void;
  entityWithPermissions: WithPermissions<Entity>;
  onAliasChange?: (alias: string) => void;
  align?: React.ComponentProps<typeof ActionsMenu.Content>['align'];
  side?: React.ComponentProps<typeof ActionsMenu.Content>['side'];
  onMenuClose: () => void;
  manageTagsAttributeNames?: string[];
};

export const DetailsDropdownMenuContentContainer = React.forwardRef<
  React.ElementRef<typeof DetailsDropdownMenuContent>,
  DetailsDropdownMenuContentContainerProps
>(function DetailsDropdownMenuContentContainer(
  {
    name,
    alias,
    isAliasEditable,
    isColorEditable = true,
    isSaving,
    onCopy,
    entityWithPermissions,
    onAliasChange,
    align,
    side,
    onMenuClose,
    manageTagsAttributeNames = DEFAULT_MANAGE_TAGS_ATTRIBUTE_NAMES,
  },
  ref,
) {
  const dispatch = useDispatch();
  const { organizationName, projectName, type, attributes, canModify } = entityWithPermissions;
  const projectIdentifier = makeProjectIdentifier(organizationName, projectName);

  const { openWithData } = useGenericModal<EntityDetailsModalData>(
    EntityTypeToDetailsModalName[type],
  );
  const { getColor, setColor, makeColorKey } = useRunsColorsContext();
  const { presetColors, addPresetColor } = usePresetColorsContext();
  const { experimentsOnlyEnabled } = useExperimentsModeContext();
  const { onShowAssociatedRuns } = useAssociatedRunsContext();

  const { removeFromCompare, autoCompareLimit, isSelectedAsExperiment, isSelectedAsRun } =
    useCompareContext();

  const { archiveEntity, unarchiveEntity } = useArchiveEntity(
    projectIdentifier,
    // We should not remove from compare if we're using auto compare
    autoCompareLimit ? undefined : removeFromCompare,
  );

  const pair = getRunIdentificationPair(entityWithPermissions);
  const isSelected = isSelectedAsExperiment(pair?.experimentName) || isSelectedAsRun(pair?.runId);

  let runIdentificationKey = experimentsOnlyEnabled ? pair?.experimentName : pair?.runId;

  if (pair && isSelected) {
    runIdentificationKey = isSelectedAsExperiment(pair.experimentName)
      ? pair.experimentName
      : pair.runId;
  }

  const shortId = getEntityShortId(entityWithPermissions);
  const isArchived = getEntityArchived(entityWithPermissions);
  const isExperimentHead = getIsExperimentHeadFromAttributes(attributes) === true;
  const yodaJobUrl = getEntityYodaJobUrl(entityWithPermissions);

  const colorKey = runIdentificationKey
    ? makeColorKey(organizationName, projectName, runIdentificationKey)
    : undefined;

  const handleColorChange = React.useCallback(
    (color: string) => {
      if (!colorKey) {
        return;
      }

      setColor(colorKey, color);
    },
    [setColor, colorKey],
  );

  const handleCopy = React.useCallback(
    async (value: string) => {
      await navigator.clipboard.writeText(value);
      dispatch(addNotification('success', 'Copied to clipboard'));
      onCopy?.();
    },
    [dispatch, onCopy],
  );

  const handleShowDetails = React.useCallback(() => {
    const { id, canModify, canDelete, canAbort } = entityWithPermissions;
    openWithData({ entityId: id, canModify, canDelete, canAbort });
  }, [entityWithPermissions, openWithData]);

  const handleOpenYoda = React.useCallback(() => {
    window.open(yodaJobUrl, '_blank', 'noopener,noreferrer');
  }, [yodaJobUrl]);

  const handleManageTags = React.useCallback(() => {
    dispatch(
      openManageTags(
        [
          {
            id: shortId,
            projectIdentifier,
          },
        ],
        manageTagsAttributeNames,
      ),
    );
  }, [dispatch, manageTagsAttributeNames, projectIdentifier, shortId]);
  const canManageTags = canModify && !isArchived;

  const canManageColor = isColorEditable && !isArchived;

  const canArchive = canModify && !isArchived;

  const canUnarchive = canModify && isArchived;

  const handleArchiveWithConfirmation = React.useCallback(() => {
    if (isExperimentHead && !shouldSkipArchiveExperimentHeadConfirmation()) {
      onMenuClose();
      dispatch(
        openConfirmationModal(ARCHIVE_EXPERIMENT_HEAD_CONFIRMATION_MODAL_TYPE, {
          projectIdentifier,
          shortIds: [shortId],
        }),
      );
      return;
    }

    archiveEntity(shortId);
  }, [archiveEntity, dispatch, isExperimentHead, onMenuClose, projectIdentifier, shortId]);

  const handleUnarchive = React.useCallback(
    async () => unarchiveEntity(shortId),
    [unarchiveEntity, shortId],
  );

  const handleShowAssociatedRuns = React.useCallback(() => {
    if (onShowAssociatedRuns && name) {
      onShowAssociatedRuns(name);
    }
  }, [onShowAssociatedRuns, name]);

  return (
    <DetailsDropdownMenuContent
      ref={ref}
      align={align}
      side={side}
      namePlaceholder={name}
      name={alias}
      isSaving={isSaving}
      colorInput={
        canManageColor && (
          <ColorPickerMenu
            label={`Change ${ENTITY_TYPE_TO_TEXT_MAP[type]} color`}
            color={colorKey ? getColor(colorKey) : undefined}
            dataRole={`change-${type}-color`}
            presetColors={presetColors}
            onChange={handleColorChange}
            onAddPresetColor={addPresetColor}
          />
        )
      }
      entityType={type}
      onShowDetails={handleShowDetails}
      onOpenYoda={yodaJobUrl ? handleOpenYoda : undefined}
      onCopy={name ? () => handleCopy(name) : undefined}
      onCopyAlias={alias ? () => handleCopy(alias) : undefined}
      onManageTags={canManageTags ? handleManageTags : undefined}
      onArchive={canArchive ? handleArchiveWithConfirmation : undefined}
      onUnarchive={canUnarchive ? handleUnarchive : undefined}
      onRename={onAliasChange}
      disableInput={!isAliasEditable}
      onRenameEnter={onMenuClose}
      onShowAssociatedRuns={onShowAssociatedRuns && name ? handleShowAssociatedRuns : undefined}
    />
  );
});
